import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, XCircle, Lightbulb, ChevronLeft, ChevronRight, Shuffle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEMO_TOPIC = {
  id: 'demo-topic',
  topic: 'Technology and Education',
  question: 'Some people believe that technology has made education more accessible and effective, while others argue it has created distractions. Discuss both views and give your opinion.',
  task_type: 2
};

const DEMO_ESSAY = `Technology has, in many different ways, significantly transformed the manner in which we learn and teach in modern educational settings. Digital tools and online resources have made information more accessible than ever before, allowing students to explore subjects beyond the constraints of traditional textbooks.

Furthermore, the integration of technology in classrooms has enabled students to access a wealth of information. Interactive learning platforms, educational apps, and virtual simulations provide engaging ways to understand complex concepts. This has particularly benefited students with different learning styles who may struggle with conventional teaching methods.

While technology offers numerous benefits to education, it also presents certain challenges that cannot be ignored. The digital divide means that not all students have equal access to technology, potentially widening the gap between privileged and underprivileged learners. Additionally, over-reliance on technology may hinder the development of critical thinking skills if not properly balanced with traditional learning approaches.

In conclusion, technology has made education more accessible to many people. However, it should be viewed as a complementary tool rather than a complete replacement for traditional educational methods. The most effective approach likely combines technological innovation with proven teaching practices, ensuring that students receive a well-rounded education that prepares them for the digital age while maintaining essential human elements of learning.`;

const DEMO_FEEDBACK = {
  overall_score: 6.5,
  task_achievement_score: 6,
  coherence_cohesion_score: 7,
  lexical_resource_score: 6,
  grammatical_range_score: 7,
  feedback: {
    strengths: [
      {
        explanation: "You've used appropriate linking words to connect your ideas.",
        skill_demonstrated: "Coherence",
        quote: "Furthermore, the integration of technology in classrooms has enabled students to access a wealth of information."
      },
      {
        explanation: "You've presented a balanced view of the topic.",
        skill_demonstrated: "Task Achievement",
        quote: "While technology offers numerous benefits to education, it also presents certain challenges that cannot be ignored."
      }
    ],
    weaknesses: [
      {
        issue: "Some sentences could be more concise.",
        improvement: "Technology has significantly transformed how we learn and teach.",
        quote: "Technology has, in many different ways, significantly transformed the manner in which we learn and teach in modern educational settings."
      },
      {
        issue: "More specific examples would strengthen your argument.",
        improvement: "For instance, interactive learning apps like Duolingo have made language learning more accessible.",
        quote: "Technology has made education more accessible to many people."
      }
    ],
    suggested_improvements: [
      {
        area: "Vocabulary",
        suggestion: "Try to use more academic vocabulary and less repetition.",
        example: "Instead of 'very important', consider terms like 'crucial', 'essential', or 'fundamental'."
      },
      {
        area: "Grammar",
        suggestion: "Vary your sentence structures more to demonstrate grammatical range.",
        example: "Consider using more complex structures like 'Not only has technology improved access to education, but it has also enhanced the quality of learning materials.'"
      }
    ]
  },
  highlighted_phrases: {
    effective_vocabulary: [
      "integration of technology",
      "wealth of information"
    ],
    good_structures: [
      "While technology offers numerous benefits to education, it also presents certain challenges that cannot be ignored."
    ],
    areas_to_improve: [
      "Technology has, in many different ways, significantly transformed"
    ]
  }
};

export const TutorialDemoMode: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const essayContent = DEMO_ESSAY;
  const wordCount = essayContent.trim().split(/\s+/).length;

  useEffect(() => {
    const feedbackTimer = setTimeout(() => {
      setIsSubmitting(true);

      setTimeout(() => {
        setShowFeedback(true);
        setIsSubmitting(false);
      }, 150);
    }, 300);

    return () => clearTimeout(feedbackTimer);
  }, []);

  return (
    <div className="space-y-6 mb-8">
      {/* Topic Selector - Exact match to the real component */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 topic-selector">
        <div className="space-y-5">
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-500 dark:text-stone-500">Topic</label>
            <Select value={DEMO_TOPIC.topic} onValueChange={() => { }}>
              <SelectTrigger data-topic-selector="true" className="dark:bg-stone-800 dark:text-white dark:border-stone-700">
                <SelectValue placeholder="Choose a topic" />
              </SelectTrigger>
              <SelectContent className="dark:bg-stone-800 dark:border-stone-700">
                <SelectItem value={DEMO_TOPIC.topic} className="dark:text-white dark:focus:bg-stone-700 dark:data-[highlighted]:bg-stone-700">
                  {DEMO_TOPIC.topic}
                </SelectItem>
                <SelectItem value="Environment" className="dark:text-white dark:focus:bg-stone-700 dark:data-[highlighted]:bg-stone-700">
                  Environment
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-500 dark:text-stone-500">Question</label>
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="outline"
                size="icon"
                className="dark:bg-stone-800 dark:hover:bg-stone-700 dark:border-stone-700"
              >
                <ChevronLeft className="h-4 w-4 dark:text-white" />
              </Button>
              <div className="grow text-center text-stone-500 dark:text-stone-400 text-sm">
                Question 1 of 10
              </div>
              <Button
                variant="outline"
                size="icon"
                className="dark:bg-stone-800 dark:hover:bg-stone-700 dark:border-stone-700"
              >
                <ChevronRight className="h-4 w-4 dark:text-white" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex flex-col items-center justify-between h-full">
            <div className="h-1/3">
              <Separator orientation="vertical" className="h-full dark:bg-stone-700" />
            </div>
            <span className="text-gray-500 dark:text-stone-500">or</span>
            <div className="h-1/3">
              <Separator orientation="vertical" className="h-full dark:bg-stone-700" />
            </div>
          </div>
          <Button
            variant="link"
            className="p-0 text-orange-500 dark:text-orange-500 hover:no-underline"
          >
            <Shuffle className="h-4 w-4 mr-2" />
            Get A Random Question
          </Button>
        </div>
      </div>

      {/* Question Display */}
      <Card className="border-stone-200 dark:border-stone-700">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl font-semibold dark:text-white">
              {DEMO_TOPIC.topic}
            </CardTitle>
            <Badge variant="outline" className="text-orange-500 border-orange-500">
              Task {DEMO_TOPIC.task_type}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-stone-700 dark:text-stone-300">{DEMO_TOPIC.question}</p>
        </CardContent>
      </Card>

      {/* Writing Area */}
      <div className="writing-area">
        <Card className="border-stone-200 dark:border-stone-700">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg font-semibold dark:text-white">
                Your Response
              </CardTitle>
              <div className="text-sm text-stone-500">
                {wordCount} words
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Write your response here..."
              className="min-h-[200px] resize-none border-stone-200 dark:border-stone-700"
              value={essayContent}
              readOnly={true} /* Make it read-only since we're using pre-filled content */
            />
            <div className="mt-4 flex justify-end">
              <Button
                disabled={isSubmitting}
                className="submit-button bg-orange-500 hover:bg-orange-600"
              >
                Submitting...
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feedback Section - with feedback-container class for step 8 */}
      {showFeedback && (
        <div className="space-y-6 feedback-section feedback-container">
          <h2 className="text-xl font-semibold dark:text-white">AI Feedback</h2>
          <div className="p-4 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg">
            <p className="text-stone-700 dark:text-stone-300">
              Technology has, in many different ways, significantly transformed the manner in which we learn and teach in modern educational settings.
              <span className="bg-green-100 dark:bg-green-900/30">Furthermore, the integration of technology in classrooms has enabled students to access a wealth of information.</span>
              <span className="bg-purple-100 dark:bg-purple-900/30">While technology offers numerous benefits to education, it also presents certain challenges that cannot be ignored.</span>
              <span className="bg-red-100 dark:bg-red-900/30">Technology has made education more accessible to many people.</span>
            </p>
          </div>

          <Separator className="dark:border-stone-600" />

          <div className="grid gap-4 grid-cols-1 md:grid-cols-5">
            <div className="text-center col-span-1 md:col-span-1 bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
              <div className="text-xl font-semibold dark:text-white">{DEMO_FEEDBACK.overall_score}</div>
              <div className="text-sm text-stone-500 dark:text-stone-500">Overall</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 col-span-1 md:col-span-4 gap-4">
              <div className="text-center bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
                <div className="text-xl font-semibold dark:text-white">{DEMO_FEEDBACK.task_achievement_score}</div>
                <div className="text-sm text-stone-500 dark:text-stone-500">Task Achievement</div>
              </div>
              <div className="text-center bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
                <div className="text-xl font-semibold dark:text-white">{DEMO_FEEDBACK.coherence_cohesion_score}</div>
                <div className="text-sm text-stone-500 dark:text-stone-500">Coherence</div>
              </div>
              <div className="text-center bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
                <div className="text-xl font-semibold dark:text-white">{DEMO_FEEDBACK.lexical_resource_score}</div>
                <div className="text-sm text-stone-500 dark:text-stone-500">Lexical</div>
              </div>
              <div className="text-center bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
                <div className="text-xl font-semibold dark:text-white">{DEMO_FEEDBACK.grammatical_range_score}</div>
                <div className="text-sm text-stone-500 dark:text-stone-500">Grammar</div>
              </div>
            </div>
          </div>

          <Separator className="dark:border-stone-600" />

          <div className="space-y-8">
            <div>
              <h4 className="flex items-center gap-2 font-semibold mb-4 dark:text-white">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Strengths
              </h4>
              <ul className="space-y-8">
                {DEMO_FEEDBACK.feedback.strengths.map((strength, index) => (
                  <li key={index} className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-4 md:items-center">
                      <p className="text-sm leading-relaxed text-stone-900 dark:text-white">{strength.explanation}</p>
                      <p className="text-xs text-stone-500 dark:text-stone-500 whitespace-nowrap">
                        Skill: <span className="font-semibold">{strength.skill_demonstrated}</span>
                      </p>
                    </div>
                    <blockquote className="border-l-2 border-green-500 pl-4 italic text-sm text-stone-500 dark:text-stone-500">
                      "{strength.quote}"
                    </blockquote>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="flex items-center gap-2 font-semibold mb-4 dark:text-white">
                <XCircle className="h-5 w-5 text-red-500" />
                Areas for Improvement
              </h4>
              <ul className="space-y-8">
                {DEMO_FEEDBACK.feedback.weaknesses.map((weakness, index) => (
                  <li key={index} className="space-y-4">
                    <div className="flex flex-col space-y-4">
                      <p className="text-sm leading-relaxed text-stone-900 dark:text-white">{weakness.issue}</p>
                      <p className="text-sm leading-relaxed text-green-700 dark:text-green-400">
                        Suggested rewrite: {weakness.improvement}
                      </p>
                    </div>
                    <blockquote className="border-l-2 border-red-500 pl-4 italic text-sm text-stone-500 dark:text-stone-500">
                      "{weakness.quote}"
                    </blockquote>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="flex items-center gap-2 font-semibold mb-4 dark:text-white">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Suggestions
              </h4>
              <ul className="space-y-8">
                {DEMO_FEEDBACK.feedback.suggested_improvements.map((improvement, index) => (
                  <li key={index} className="space-y-4">
                    <div className="flex flex-col space-y-4">
                      <h5 className="text-sm font-medium capitalize text-stone-900 dark:text-white">{improvement.area}</h5>
                      <p className="text-sm leading-relaxed text-stone-900 dark:text-white">{improvement.suggestion}</p>
                    </div>
                    <blockquote className="border-l-2 border-yellow-500 pl-4 italic text-sm text-stone-500 dark:text-stone-500">
                      Example: "{improvement.example}"
                    </blockquote>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-3 bg-orange-50 dark:bg-stone-700 rounded-md">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-orange-500 mr-2 mt-0.5" />
                <p className="text-sm text-stone-700 dark:text-stone-300">
                  <strong>Tutorial Note:</strong> This is a simplified demo of the AI feedback you'll receive.
                  The actual feedback will be more detailed and personalized to your writing.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
